import React, { useEffect, useState } from "react";
import { getForumQuotaStatus } from "../utils/forumQuotaUtils";
import { useLanguage } from "../contexts/LanguageContext";

const ForumQuotaStatus = ({
  userId,
  subscription,
  refreshKey = 0,
}) => {
  const { t } = useLanguage();

  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadQuota = async () => {
      if (!userId) {
        setQuota(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const result = await getForumQuotaStatus({
          userId,
          subscription,
        });

        if (!cancelled) {
          setQuota(result);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement du quota Forum :",
          error
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadQuota();

    return () => {
      cancelled = true;
    };
  }, [userId, subscription, refreshKey]);

  if (!userId || loading || !quota) {
    return null;
  }

  const isUnlimited =
    quota.remainingDaily === Infinity &&
    quota.remaining30Days === Infinity;

  const getPercentage = (count, limit) => {
    if (!Number.isFinite(limit) || limit <= 0) {
      return 0;
    }

    return Math.min((count / limit) * 100, 100);
  };

  const dailyPercentage = getPercentage(
    quota.dailyCount,
    quota.limits.daily
  );

  const monthlyPercentage = getPercentage(
    quota.rolling30DaysCount,
    quota.limits.rolling30Days
  );

  const tierLabel = t(
    `navigation.subscriptionStatus.${quota.effectiveTier}`
  );

  return (
    <section className="forum-quota-status">
      <div className="forum-quota-status__header">
        <span className="forum-quota-status__title">
          🎬 {t("forumQuota.reserveTitle")}
        </span>

        <span
          className="forum-quota-status__tier"
          data-tier={quota.effectiveTier}
        >
          {tierLabel}
        </span>
      </div>

      {isUnlimited ? (
        <div className="forum-quota-status__unlimited">
          <span className="forum-quota-status__infinity">
            ∞
          </span>

          <span>
            {t("forumQuota.unlimited")}
          </span>
        </div>
      ) : (
        <div className="forum-quota-status__gauges">
          <QuotaGauge
            label={t("forumQuota.last24Hours")}
            count={quota.dailyCount}
            limit={quota.limits.daily}
            remaining={quota.remainingDaily}
            percentage={dailyPercentage}
            remainingLabel={t("forumQuota.remaining")}
          />

          <QuotaGauge
            label={t("forumQuota.last30Days")}
            count={quota.rolling30DaysCount}
            limit={quota.limits.rolling30Days}
            remaining={quota.remaining30Days}
            percentage={monthlyPercentage}
            remainingLabel={t("forumQuota.remaining")}
          />
        </div>
      )}
    </section>
  );
};

const QuotaGauge = ({
  label,
  count,
  limit,
  remaining,
  percentage,
  remainingLabel,
}) => {
  return (
    <div className="forum-quota-gauge">
      <div className="forum-quota-gauge__infos">
        <span>{label}</span>

        <strong>
          {count} / {limit}
        </strong>
      </div>

      <div className="forum-quota-gauge__track">
        <div
          className="forum-quota-gauge__fill"
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>

      <div className="forum-quota-gauge__remaining">
        {remaining} {remainingLabel}
      </div>
    </div>
  );
};

export default ForumQuotaStatus;